import React, { useState } from "react";
import axios from "axios";
import { Loader2, Gift, Copy, Check, Share2 } from "lucide-react";
import { TOK, SERIF, SANS } from "../../pages/Soulumo/SignatureBits";

const API = process.env.REACT_APP_BACKEND_URL;

const ShareRow = ({ url, toName }) => {
  const [copied, setCopied] = useState(false);
  const text = `${toName ? `${toName}, ` : ""}I recorded something for you 💛 ${url}`;
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch { /* ignore */ }
  };
  const share = () => (navigator.share ? navigator.share({ title: "A message for you", text, url }).catch(() => {}) : copy());
  const btn = "inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-[0.62rem] uppercase tracking-[0.18em]";
  return (
    <div className="mt-4 space-y-3" data-testid="voice-gift-result">
      <div className="text-xs break-all" style={{ color: TOK.creamDim }} data-testid="voice-gift-link">{url}</div>
      <div className="flex flex-wrap gap-2">
        <a href={`https://wa.me/?text=${encodeURIComponent(text)}`} target="_blank" rel="noreferrer" data-testid="voice-gift-whatsapp"
          className={btn} style={{ background: TOK.gold, color: TOK.ink, fontFamily: SANS }}>WhatsApp</a>
        <a href={`sms:?&body=${encodeURIComponent(text)}`} data-testid="voice-gift-sms" className={btn}
          style={{ border: `1px solid ${TOK.gold}`, color: TOK.gold, fontFamily: SANS }}>Text</a>
        <button type="button" onClick={share} data-testid="voice-gift-share" className={btn}
          style={{ border: `1px solid ${TOK.goldMist}`, color: TOK.creamDim, fontFamily: SANS }}><Share2 size={12} /> Share</button>
        <button type="button" onClick={copy} data-testid="voice-gift-copy" className={btn}
          style={{ border: `1px solid ${TOK.goldMist}`, color: TOK.creamDim, fontFamily: SANS }}>
          {copied ? <Check size={12} /> : <Copy size={12} />} {copied ? "Copied" : "Copy link"}
        </button>
      </div>
    </div>
  );
};

// A short message in the user's cloned voice, shared by private link.
export const VoiceGiftComposer = () => {
  const [toName, setToName] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [url, setUrl] = useState("");
  const [err, setErr] = useState("");

  const create = async () => {
    setBusy(true); setErr(""); setUrl("");
    try {
      const r = await axios.post(`${API}/api/voice-clone/gifts`, { message: msg, to_name: toName },
        { headers: { Authorization: `Bearer ${localStorage.getItem("reclaim_token")}` } });
      setUrl(`${window.location.origin}${r.data.path}`);
    } catch (e) {
      const d = e?.response?.data?.detail || "";
      setErr(d.startsWith("allowance_reached") ? "Your voice allowance for this month is used up — it resets on the 1st." : "We couldn't create your message. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const field = { border: `1px solid ${TOK.goldMist}`, color: TOK.cream };
  return (
    <section className="mt-12" data-testid="voice-gift-composer">
      <div className="text-[0.6rem] uppercase tracking-[0.32em] mb-3 inline-flex items-center gap-2" style={{ color: TOK.goldSoft, fontFamily: SANS }}>
        <Gift size={12} /> Send a voice note
      </div>
      <p className="italic mb-4" style={{ color: TOK.creamDim, fontFamily: SERIF }}>Write a few words for someone you love. They&apos;ll hear it in your voice, with no app or login needed.</p>
      <input value={toName} onChange={(e) => setToName(e.target.value.slice(0, 40))} placeholder="Who is it for? (optional)" data-testid="voice-gift-to"
        className="w-full rounded-full px-4 py-2.5 mb-3 bg-transparent focus:outline-none text-sm" style={field} />
      <textarea value={msg} onChange={(e) => setMsg(e.target.value.slice(0, 300))} rows={3} placeholder="Happy birthday. I'm so proud of you…" data-testid="voice-gift-message"
        className="w-full rounded-2xl px-4 py-3 bg-transparent focus:outline-none text-[1.05rem]" style={{ ...field, fontFamily: SERIF }} />
      <div className="flex items-center justify-between mt-2">
        <span className="text-xs" style={{ color: TOK.creamMist }} data-testid="voice-gift-count">{msg.length} / 300</span>
        <button type="button" onClick={create} disabled={busy || msg.trim().length < 3} data-testid="voice-gift-create-btn"
          className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-[0.68rem] uppercase tracking-[0.22em] disabled:opacity-40"
          style={{ background: TOK.gold, color: TOK.ink, fontFamily: SANS, minHeight: 48 }}>
          {busy ? <Loader2 size={13} className="animate-spin" /> : <Gift size={13} />} Create voice note
        </button>
      </div>
      {err && <p className="mt-3 text-sm" style={{ color: "#dc8b6c" }} data-testid="voice-gift-error">{err}</p>}
      {url && <ShareRow url={url} toName={toName} />}
    </section>
  );
};
